import { type ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GlobalSpinner } from '../GlobalSpinner';
import { useSocket } from '../AppShell/SocketProvider';
import type { PageReducerState, PageState } from './types';
import useDispatchActions from './useDispatchActions';
import useFetchData from './useFetchData';
import useRedirect from './useRedirect';

export {
  default as createController,
  type AsyncActionDefinition,
  type FetchDataParams,
} from './createController';

type PageChildrenProps<TState> = {
  pageState: PageState;
  data: TState;
  actions: Record<string, (...args: unknown[]) => Promise<unknown>>;
};

type PageProps<TState> = {
  // Loose at this seam — every controller's TState differs and the consumer
  // narrows via the children render-prop. Same approach as the canonical.
  controller: {
    name: string;
    actions: Record<string, (...args: unknown[]) => unknown>;
    fetchData: (args: unknown) => unknown;
    pageActions: { clearNavigateTo: () => { type: string } };
    socketListeners?: Record<string, (message: unknown) => unknown>;
  };
  children: (props: PageChildrenProps<TState>) => ReactNode;
  // Renders above the load/error gate — stays visible while data loads or if the fetch fails.
  header?: (props: PageChildrenProps<TState>) => ReactNode;
  /** Optional override for the default GlobalSpinner shown while loading. */
  loadingFallback?: ReactNode;
  /** Optional override for the default error fallback. */
  errorFallback?: ReactNode;
};

function Page<TState>({
  controller,
  children,
  header,
  loadingFallback,
  errorFallback,
}: PageProps<TState>) {
  const state = useSelector((s: Record<string, unknown>) => s[controller.name]) as
    PageReducerState<TState> | undefined;

  const dispatch = useDispatch();
  const { socket } = useSocket();

  if (state === undefined) {
    throw new Error(`Page component requires a reducer for ${controller.name} to be defined`);
  }

  const pageState = state.pageState;

  const dispatchActions = useDispatchActions(controller as never);

  useRedirect({
    pageState,
    clearNavigateTo: controller.pageActions.clearNavigateTo,
  });

  useFetchData({
    controller: controller as never,
    pageState,
  });

  // Socket listeners — subscribe via the raw socket.io-client Socket exposed
  // by SocketProvider. Same effect as the canonical's `addMessageListener`,
  // one fewer indirection. Skipped when the controller declares none or
  // before the socket is connected (it'll resubscribe on the next render
  // once `socket` becomes non-null).
  useEffect(() => {
    if (!controller.socketListeners || !socket) return;

    const cleanups = Object.entries(controller.socketListeners).map(([eventType, listener]) => {
      const handler = (message: unknown) => {
        (dispatch as (a: unknown) => unknown)(listener(message));
      };
      socket.on(eventType, handler);
      return () => {
        socket.off(eventType, handler);
      };
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [controller.socketListeners, socket, dispatch, controller.name]);

  const renderProps = { pageState, data: state.data, actions: dispatchActions };

  const body = !pageState.loaded
    ? (loadingFallback ?? (
        <div className="flex min-h-screen items-center justify-center">
          <GlobalSpinner size="lg" />
        </div>
      ))
    : pageState.errorOnLoad
      ? (errorFallback ?? (
          <div className="flex min-h-screen items-center justify-center px-6 text-center">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Failed to load</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                We couldn&apos;t load this page. Please try again later.
              </p>
            </div>
          </div>
        ))
      : children(renderProps);

  return (
    <>
      {header?.(renderProps)}
      {body}
    </>
  );
}

export default Page;
